import { Capacitor } from "@capacitor/core";
import { IRocketTokens, IRoom, ISubscription, spErrorTags, spLogTags, spWarnTags } from "@eventr/common";
import { ApiClient } from "../../data/apiClientCtor";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { isChatAvailable } from "feat/chatpool/chat-eligibility";
import { RocketMessageId, RocketSocketStatus } from "state/rocketchat/rocketchat-utils";
import {
  setLoadingChat,
  setRcUserInfo,
  setRcUserList,
  setRoomsFetched,
  setUserRooms,
  setUserSubscriptions,
  useChatDataStore,
} from "./chatData.store";
import { useShallow } from "zustand/react/shallow";
import {
  EditChannelUsersBodyType,
  IRCUser,
  IRCUserInfo,
  RocketAPI,
  RocketChatBodyType,
  RocketCreateChannelBodyType,
  RocketCreateDirectChannelBodyType,
} from "../../data/Rocket";
import i18n from "../../i18n/i18next";
import { bs, useBoundStore } from "../../state/bound.store";
import { isIosToastAvailable } from "../../state/overlayState/native-toast";
import { defaultAddSnack } from "../../state/overlayState/overlayContext";
import { BackoffOptions, createRetryLoop } from "../../state/rocketchat/backoff";
import { selectRocketChatChannels } from "../../state/rocketchat/rocketchat.slice";
import { isSocketConnectingOrOpen, isSocketOpen } from "../../state/rocketchat/socket-state";
import { resetReconnectBackoff } from "../../state/rocketchat/socket-supervisor";
import { getLiveToken, useLiveToken, useRocketchatUiStore } from "../../state/rocketchatState/rocketchatUi.store";
import { getMobileOperatingSystem, getRocketChatUrl } from "../../utils";
import { GUEST_ORG_NAME } from "../../consts";
import { useGuestStore } from "../guest/guest.store";
import { routes } from "routing";
import { useGlobalRouter } from "../globalRouter/useGlobalRouter";
import { getChatCache, getChatStore } from "./chatStore";
import { createRocketSnack } from "./createRocketSnack";
import { ROCKET_ENDPOINTS } from "./endpoints";
import { startNativeToastTapListener, stopNativeToastTapListener } from "./native-toast-tap-listener";

export const createGroupChannel = async (data: RocketCreateChannelBodyType) => {
  spLogTags(`Creating new group channel: ${data.channel_name}`, ["rocketchat"]);
  const resp = await RocketAPI.createGroupChannel(data);
  spLogTags(resp, ["rocketchat"]);
  return resp;
};

export const deleteGroupChannel = async (channelId: string) => {
  spLogTags(`Deleting group channel: ${channelId}`, ["rocketchat"]);
  const resp = await RocketAPI.deleteGroupChannel({ roomId: channelId });
  spLogTags(resp, ["rocketchat"]);
  return resp;
};

export const createChannel = async (data: RocketChatBodyType) => {
  spLogTags(`Creating new channel: ${data.channel_name}`, ["rocketchat"]);
  const resp = await RocketAPI.createChannel(data);
  spLogTags(resp, ["rocketchat"]);
  return resp;
};

export const removeUsersFromGroupChannel = async (data: EditChannelUsersBodyType) => {
  spLogTags(`Removing users from channel: ${data.channel_id}`, ["rocketchat"]);
  const resp = await RocketAPI.removeUsersFromGroupChannel(data);
  spLogTags(resp, ["rocketchat"]);
  return resp;
};

export const addUsersToChannel = async (data: EditChannelUsersBodyType) => {
  spLogTags(`Adding users to channel: ${data.channel_id}`, ["rocketchat"]);
  const resp = await RocketAPI.addUsersToChannel(data);
  spLogTags(resp, ["rocketchat"]);
  return resp;
};

interface IRocketChatContext {
  createDirectChannel: (data: RocketCreateDirectChannelBodyType) => Promise<any>;
  openChat: (rid?: string, type?: string) => Promise<void>;
  setLoadingChat: (chatLoading: boolean) => void;
  getRooms: () => void;
  setRooms: (room: string) => void;
  getSubscribedRooms: () => void;
  markAsRead: (rid: string) => Promise<boolean>;
  userRooms: IRoom[];
  /** True once rooms/get has returned. Cache hydration does not set it. */
  roomsFetched: boolean;
  userSubscriptions: ISubscription[];
  rcUserList: IRCUser[];
  rcUserInfo: IRCUserInfo | undefined;
  loadingChat: boolean;
  isActivated: boolean;
  selectedItineraryUnreadCount: number;
}

const RocketChatContext = createContext<IRocketChatContext>({
  createDirectChannel: (_data: RocketCreateDirectChannelBodyType) => Promise.resolve({}),
  openChat: () => Promise.resolve(),
  setLoadingChat: () => {},
  getRooms: () => {},
  setRooms: () => {},
  getSubscribedRooms: () => {},
  markAsRead: () => Promise.resolve(false),
  userRooms: [],
  roomsFetched: false,
  userSubscriptions: [],
  rcUserList: [],
  rcUserInfo: undefined,
  loadingChat: false,
  isActivated: false,
  selectedItineraryUnreadCount: 0,
});

/** Token fetches retry on failure, capped so a long RocketChat outage stays cheap. */
const TOKEN_RETRY_BACKOFF: BackoffOptions = { baseMs: 2000, capMs: 60000 };

const tokenRetryLoop = createRetryLoop(
  TOKEN_RETRY_BACKOFF,
  (delayMs) => `RCPROVIDER | no usable RocketChat token, refetching in ${delayMs}ms`,
  (message) => spWarnTags(message, ["rocketchat"]),
);

/** One request in flight per room, across every copy of the provider. */
const markAsReadInFlight = new Map<string, Promise<boolean>>();

export const RocketChatProvider = (props: { children: React.ReactNode }) => {
  const {
    rocketWebsocket,
    rocketSocketStatus,
    orgConfig,
    wpUserId,
    selectedOrgName,
    rcTokens,
    chatNotificationPreference,
    pushToken,
  } = useBoundStore(
    useShallow((s) => {
      return {
        rocketWebsocket: s.rocketWebsocket,
        rocketSocketStatus: s.rocketSocketStatus,
        orgConfig: s.orgConfig,
        wpUserId: s.wpUser?.id,
        selectedOrgName: s.selectedOrg?.name,
        rcTokens: s.rcTokens,
        chatNotificationPreference: s.chatNotificationPreference,
        pushToken: s.pushToken,
      };
    }),
  );

  const { pushPath } = useGlobalRouter();
  const rocketChannels = useBoundStore(selectRocketChatChannels);
  const isGuestMode = useGuestStore((s) => s.isGuestMode);

  const apiClient = useMemo(() => new ApiClient({ baseURL: "" }), []);

  // Shared across every mounted copy of this provider (one per native view on iOS); see
  // chatData.store.ts. The setters are module-level and stable, used exactly as the old
  // useState setters were.
  const { userRooms, userSubscriptions, rcUserList, rcUserInfo, loadingChat, roomsFetched } = useChatDataStore(
    useShallow((s) => ({
      userRooms: s.userRooms,
      userSubscriptions: s.userSubscriptions,
      rcUserList: s.rcUserList,
      rcUserInfo: s.rcUserInfo,
      loadingChat: s.loadingChat,
      roomsFetched: s.roomsFetched,
    })),
  );

  // One mounted copy - the leader - runs the effects below that talk to the socket and the
  // servers; the others only render the shared data and navigate in their own view. The first
  // to mount claims it, and when the leader unmounts the next copy to re-render takes over.
  const instanceId = useRef<symbol>(Symbol("RocketChatProvider"));
  const leader = useChatDataStore((s) => s.leader);
  const isLeader = leader === instanceId.current;
  useEffect(() => {
    const me = instanceId.current;
    if (!useChatDataStore.getState().leader) useChatDataStore.setState({ leader: me });
    return () => {
      if (useChatDataStore.getState().leader === me) useChatDataStore.setState({ leader: null });
    };
  }, [leader]);

  const updateUserRooms = useCallback(async (userRooms: IRoom[]) => {
    setUserRooms(userRooms);
    setRoomsFetched(true);
    await getChatStore()
      ?.set("rooms", "rcRooms-" + getLiveToken()?.org, userRooms)
      .catch((error) => {
        spErrorTags("Error setting user rooms", ["rocketchat"], error);
      });
  }, []);

  const updateUserSubscriptions = useCallback(async (userSubscriptions: ISubscription[]) => {
    setUserSubscriptions(userSubscriptions);
    await getChatStore()
      ?.set("subscriptions", "rcSubscriptions-" + (getLiveToken()?.org ?? ""), userSubscriptions)
      .catch((error) => {
        spErrorTags("Error setting user subscriptions", ["rocketchat"], error);
      });
  }, []);

  // The only *render* read of the live token in this file: it appears in two dep arrays
  // below, which are evaluated during render, so under signals this component subscribed
  // and both effects re-ran on a token change. Every other read here is inside a callback
  // or an effect body — untracked then, `getLiveToken()` now.
  const liveTokenValue = useLiveToken();

  // Sentinel org covers the launch window before guest-store rehydrates; isGuestMode covers after.
  const isGuestChatSession = isGuestMode || selectedOrgName === GUEST_ORG_NAME;

  const isOrgValid = selectedOrgName && !isGuestChatSession;

  // This will:
  // 1. If we do NOT have a liveToken (WebSocket not authenticated yet):
  //    - Attempt to load cached subscriptions from chatStore for offline display
  // 2. If we DO have a liveToken (authenticated):
  //    - Call getRooms() to fetch fresh room data via WebSocket
  // NOTE: A user subscription is the connection between a user and rc room
  useEffect(() => {
    if (!isLeader) return;
    if (!getLiveToken() && wpUserId && isOrgValid) {
      try {
        getChatCache("subscriptions", "rcSubscriptions-" + selectedOrgName).then((subscription: any) => {
          setUserSubscriptions(subscription);
        });
      } catch {
        spErrorTags("Could not get rc subscriptions from cache", ["rocketchat"]);
      }
    } else if (bs().rocketSocketStatus === RocketSocketStatus.AUTHENTICATED) {
      spLogTags("RCPROVIDER | WE HAVE LIVETOKEN, fetching rooms ", ["rocketchat"]);
      getRooms();
    }
    // TODO: fix, causes infinite re-render (removed getRooms() from deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeader, selectedOrgName, wpUserId, liveTokenValue, setUserSubscriptions, isOrgValid]);

  // This will:
  // If we do NOT have a liveToken (WebSocket not authenticated yet):
  //    - Attempt to load cached rooms
  useEffect(() => {
    if (!isLeader) return;
    if (!getLiveToken() && wpUserId && isOrgValid) {
      try {
        getChatCache("rooms", "rcRooms-" + selectedOrgName).then((e: any) => {
          setUserRooms(e);
        });
      } catch {
        spErrorTags("Could not get rc rooms from cache", ["rocketchat"]);
      }
    }
  }, [isLeader, selectedOrgName, wpUserId, isOrgValid]);

  const isActivated = useMemo(() => isChatAvailable(orgConfig), [orgConfig]);

  const isOpen = useCallback(() => {
    try {
      return isSocketOpen(rocketWebsocket);
    } catch {
      spErrorTags("Error with websocket", ["rocketchat"]);
      return false;
    }
  }, [rocketWebsocket]);

  // Matches the selected org against the tokens we already have, and puts the socket in the
  // state that match implies.
  const checkTokens = useCallback(() => {
    spLogTags("RCPROVIDER | checking tokens ", ["rocketchat"]);
    if (!bs().selectedOrg || !rcTokens) return;

    const orgToken = rcTokens.find((x) => x?.org === bs().selectedOrg?.name);
    if (!orgToken) {
      spWarnTags("No matching RocketChat token found for org " + bs().selectedOrg?.name, ["rocketchat"]);
      return;
    }

    const liveToken = getLiveToken();
    if (liveToken && liveToken.authToken === orgToken.authToken) {
      // Same token as last time: only reconnect if the socket has actually dropped.
      if (!isSocketConnectingOrOpen(bs().rocketWebsocket)) {
        spLogTags("RCPROVIDER | token unchanged, socket not connecting/open, opening websocket", ["rocketchat"]);
        bs().openWebsocket();
      }
    } else {
      // New token for this org: always (re)connect with it.
      useRocketchatUiStore.getState().setLiveToken(orgToken);
      spLogTags("RCPROVIDER | token changed, opening websocket", ["rocketchat"]);
      bs().openWebsocket();
    }
    setLoadingChat(false);
  }, [rcTokens]);

  // Keeps trying to obtain a usable RocketChat token for the selected org, and reconnects the
  // socket whenever the token backing it changes. checkTokens() above matches against tokens
  // already fetched; the retry loop below re-fetches the token list when no match was found.
  useEffect(() => {
    if (!isLeader) return;
    if (!isActivated || isGuestChatSession) return;

    if (rcTokens.length > 0) {
      setLoadingChat(true);
      checkTokens();
    }

    const haveUsableToken = rcTokens.length > 0 && (!isOrgValid || getLiveToken());
    if (haveUsableToken) {
      tokenRetryLoop.reset();
      return;
    }

    let cancelled = false;

    async function fetchTokens() {
      spLogTags("RCPROVIDER | getting RocketChat tokens", ["rocketchat"]);
      try {
        // The timer can fire just after another caller's fetch landed but before this effect
        // re-ran to cancel it; reusing that fresh list avoids minting a second set of sessions
        // (with spchat, a new token and an immediate reconnect).
        await bs().getRcTokens({ reuseWithinMs: 10_000 });
      } catch (error) {
        spErrorTags("Error fetching RocketChat tokens", ["rocketchat"], error);
      }
      // A successful fetch replaces rcTokens, which re-runs this effect and takes over the chain.
      if (cancelled) return;
      tokenRetryLoop.scheduleNext(() => void fetchTokens());
    }

    // Always waits out a backoff delay before fetching, even on the first attempt.
    tokenRetryLoop.scheduleNext(() => void fetchTokens());

    return () => {
      cancelled = true;
      tokenRetryLoop.cancel();
    };
  }, [isLeader, isActivated, rcTokens, isOrgValid, liveTokenValue, isGuestChatSession, checkTokens]);

  const registerPushToken = useCallback(
    async (rcToken: IRocketTokens, os: "iOS" | "Android" | "unknown") => {
      try {
        const url = getRocketChatUrl(rcToken.org);

        spLogTags(`Registering push token for rocketchat org ${rcToken.org}`, ["rocketchat"]);

        const response = await apiClient.post<any, any>(
          url + ROCKET_ENDPOINTS.registerPush,
          {
            type: os === "iOS" ? "apn" : "gcm",
            value: bs().pushToken,
            appName: "com.softpauer.eventr",
          },
          {
            headers: {
              "X-Auth-Token": rcToken.authToken,
              "X-User-Id": rcToken.userId,
            },
          },
        );

        if (response && response.status === "success" && response.data?.success) {
          spLogTags("Successfully registered push token for rocketchat", ["rocketchat"]);
        } else {
          spErrorTags("Error registering push token for rocketchat", ["rocketchat"], response);
        }
      } catch (error) {
        spErrorTags("Error registering push token for rocketchat", ["rocketchat"], error);
      }
    },
    [apiClient],
  );

  // This will:
  // Register push tokens for the rocketchat
  useEffect(() => {
    if (!isLeader) return;
    // Only a device has a push token. On the web `pushToken` is a "dummyToken" placeholder,
    // which this used to register as an Android (gcm) device: every push to that user then
    // went to it as well and failed.
    if (!Capacitor.isNativePlatform()) return;
    if ((rcTokens?.length ?? 0) > 0 && pushToken) {
      const os = getMobileOperatingSystem();

      async function registerRocketchatPushToken() {
        for (const token of rcTokens) {
          try {
            await registerPushToken(token, os);
          } catch (error) {
            spErrorTags("Error registering push token for rocketchat", ["rocketchat"], error);
          }
        }
      }

      registerRocketchatPushToken();
    }
  }, [isLeader, pushToken, rcTokens, registerPushToken]);

  async function openChat(roomId?: string, chatType?: string) {
    if (!roomId) {
      defaultAddSnack(i18n.t("snack.rocket.unableToFindChat"), "error");
      return;
    }
    const path = routes.chat.room.build({ roomType: chatType ?? "", roomId });
    spLogTags(`Navigating to page: ${path}`, ["rocketchat"]);
    await pushPath(path);
  }

  // Routes native toast taps to the same openChat used by RocketSnack.tsx's onClick.
  useEffect(() => {
    if (!isLeader) return;
    if (!isIosToastAvailable()) return;
    startNativeToastTapListener(openChat);
    return () => stopNativeToastTapListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLeader]);

  const createDirectChannel = async (data: RocketCreateDirectChannelBodyType) => {
    spLogTags(`Creating new direct chat with: ${data.user_id2} `, ["rocketchat"]);
    const resp = await RocketAPI.createDirectChannel(data);
    spLogTags(resp, ["rocketchat"]);
    getRooms();
    return resp;
  };

  function constructMethod(method: string, params?: any, id?: string) {
    return JSON.stringify({
      msg: "method",
      method: method,
      id: id ?? "8129",
      params: params,
    });
  }

  const subscribeToStream = useCallback(
    (name: string, uuid: string, params: any) => {
      if (rocketWebsocket && isOpen()) {
        try {
          rocketWebsocket.send(
            JSON.stringify({
              msg: "sub",
              id: uuid,
              name: name,
              params: params,
            }),
          );
          return uuid;
        } catch (e) {
          spErrorTags("Could not subscribe to stream", ["rocketchat"], e);
          return null;
        }
      } else {
        spErrorTags("Websocket is closed, cannot subscribe to stream", ["rocketchat"]);
        return null;
      }
    },
    [isOpen, rocketWebsocket],
  );

  // markAsRead is called from render-driven effects (chat-room re-runs it on every messages
  // change), so it must be cheap to call often: a stable identity, nothing sent when the room
  // has nothing unread, and one request in flight per room. Without these, opening a room
  // fired ~90 subscriptions.read in a few seconds and the server answered 429.
  const userSubscriptionsRef = useRef(userSubscriptions);
  userSubscriptionsRef.current = userSubscriptions;

  /** Clears the room's unread state. Resolves true only when a request was sent and succeeded. */
  const markAsRead = useCallback(
    (rid: string): Promise<boolean> => {
      const sub = userSubscriptionsRef.current?.find((s) => s.rid === rid);
      if (sub && !sub.unread && !sub.alert) return Promise.resolve(false);
      const inFlight = markAsReadInFlight.get(rid);
      if (inFlight) return inFlight;

      const request = (async () => {
        const url = getRocketChatUrl(getLiveToken()?.org ?? "");
        const resp = await apiClient
          .post(
            url + ROCKET_ENDPOINTS.readSubscriptions,
            {
              rid: rid,
            },
            {
              headers: {
                "X-Auth-Token": getLiveToken()?.authToken ?? "",
                "X-User-Id": getLiveToken()?.userId ?? "",
              },
            },
          )
          .catch((error) => {
            spErrorTags("Error marking as read", ["rocketchat"], error);
            return null;
          });
        // A rejected request has already been logged by the catch above.
        if (!resp) return false;
        if (resp.data && (resp.data as any).success) {
          return true;
        }
        spErrorTags("Error changing unread, invalid response", ["rocketchat"]);
        return false;
      })().finally(() => markAsReadInFlight.delete(rid));

      markAsReadInFlight.set(rid, request);
      return request;
    },
    [apiClient],
  );

  // This will:
  // Will fetch the rocketchat user list from the organisation rocket chat server.
  useEffect(() => {
    if (!isLeader) return;
    async function getUsersList(
      options: {
        field?: object | undefined;
      } = {
        field: undefined,
      },
    ) {
      const url = getRocketChatUrl(getLiveToken()?.org ?? "");
      const field = options?.field ? `&fields=${JSON.stringify(options.field)}` : "";
      try {
        const users = await fetch(`${url}${ROCKET_ENDPOINTS.usersList}${field}&count=0`, {
          headers: {
            "Content-Type": "application/json",
            "X-Auth-Token": getLiveToken()?.authToken ?? "",
            "X-User-Id": getLiveToken()?.userId ?? "",
          },
          method: "GET",
        });
        const resp = await users.json();
        getChatStore()?.set("users", "rcUsers-" + (getLiveToken()?.org ?? ""), resp);
        setRcUserList(resp.users);
        return resp;
      } catch (error) {
        spErrorTags("Error fetching rcUsers", ["rocketchat"], error);
      }
    }

    if (selectedOrgName && wpUserId) {
      getChatCache("users", "rcUsers-" + selectedOrgName).then((e: any) => {
        setRcUserList(e?.users);
      });

      if (getLiveToken()) {
        spLogTags("Getting RC users information and writing to chatstore", ["rocketchat"]);
        getUsersList({
          field: {
            _id: 1,
            username: 1,
            name: 1,
            status: 1,
            type: 1,
            customFields: 1,
          },
        }).then((resp) => {
          if (resp && resp.success) {
            spLogTags("getUsersList, success: " + resp.success, ["rocketchat"]);
          } else {
            spErrorTags("Failed to get RC users list", ["rocketchat"], resp);
          }
        });
      }
    }
  }, [isLeader, selectedOrgName, wpUserId]);

  // This useEffect will:
  // Will fetch the currently logged in user's rocketChat information.
  useEffect(() => {
    if (!isLeader) return;
    async function getUserInfo() {
      const url = getRocketChatUrl(getLiveToken()?.org ?? "");
      try {
        const userInfo = await fetch(url + ROCKET_ENDPOINTS.userInfo, {
          headers: {
            "Content-Type": "application/json",
            "X-Auth-Token": getLiveToken()?.authToken ?? "",
            "X-User-Id": getLiveToken()?.userId ?? "",
          },
          method: "GET",
        });
        const resp = await userInfo.json();
        getChatStore()?.set("me", "rcUserInfo-" + (getLiveToken()?.org ?? ""), resp);
        setRcUserInfo(resp);
        return resp;
      } catch (error) {
        spErrorTags("Error fetching rocketchat userInfo", ["rocketchat"], error);
      }
    }

    if (selectedOrgName && wpUserId) {
      getChatCache("me", "rcUserInfo-" + selectedOrgName).then((e: any) => {
        setRcUserInfo(e);
      });
      if (getLiveToken()) {
        spLogTags("Getting RC profile and writing to chatstore", ["rocketchat"]);
        getUserInfo().then((resp) => {
          if (resp && resp.success) {
          } else {
            spErrorTags("Failed to get RC user info", ["rocketchat"], resp);
          }
        });
      }
    }
    // liveTokenValue is a dep so userInfo refetches on token change; the body deliberately
    // reads getLiveToken() rather than the captured value, matching the untracked signal
    // read it replaced. (The old eslint-disable here is gone — with a real variable in the
    // deps instead of a `liveToken.value` expression, the rule is now satisfied.)
  }, [isLeader, selectedOrgName, wpUserId, liveTokenValue]);

  const getRooms = useCallback(
    async function (date?: number) {
      try {
        if (!selectedOrgName || !wpUserId) {
          spWarnTags("User is not logged in, cannot get rooms", ["rocketchat"]);
          return;
        }
        // The request goes out before the cache read, so a cache that never answers cannot
        // hold back the live rooms. The cache then only fills rooms nothing has set yet.
        if (rocketWebsocket && isOpen()) {
          spLogTags("RCPROVIDER | fetch rooms from rocket web socket and set cached rooms", ["rocketchat"]);
          rocketWebsocket.send(constructMethod("rooms/get", [{ $date: date ?? 0 }], RocketMessageId.getRooms));
        } else {
          spWarnTags("Websocket is closed, cannot get rooms", ["rocketchat"]);
          bs().openWebsocket();
        }
        const cachedRooms = await getChatCache<IRoom[]>("rooms", "rcRooms-" + selectedOrgName);
        if (cachedRooms) {
          setUserRooms((rooms) => rooms ?? cachedRooms);
        }
      } catch (error) {
        spErrorTags("Error getting rooms", ["rocketchat"], error);
        return;
      }
    },
    [isOpen, rocketWebsocket, selectedOrgName, wpUserId],
  );

  const getSubscribedRooms = useCallback(
    function (date?: number) {
      if (rocketWebsocket && isOpen()) {
        try {
          rocketWebsocket.send(
            constructMethod("subscriptions/get", [{ $date: date ?? 0 }], RocketMessageId.getSubscribedRooms),
          );
          return;
        } catch {
          spErrorTags("Error getting subscribed rooms", ["rocketchat"]);
          return;
        }
      } else {
        if (selectedOrgName && wpUserId) {
          spLogTags("Socket closed, fetch cache", ["rocketchat"]);
          getChatCache("rooms", "rcRooms-" + selectedOrgName).then((e: any) => {
            if (!getLiveToken() && wpUserId) {
              setUserRooms(e);
            }
          });
        }
        spWarnTags("Websocket is closed, cannot get subscribed rooms", ["rocketchat"]);
        useRocketchatUiStore.getState().setLiveToken(undefined);
        return;
      }
    },
    [isOpen, rocketWebsocket, selectedOrgName, wpUserId],
  );

  const updateRoom = useCallback(
    (message: any) => {
      if (message.msg === "changed") {
        if (message.fields && message.fields.args && message.fields.args[0] === "updated") {
          let tempUserRooms = userRooms?.filter((room) => room._id !== message.fields.args[1]._id);
          tempUserRooms = tempUserRooms?.concat(message.fields.args[1]);
          updateUserRooms(tempUserRooms ?? []);
        } else if (message.fields && message.fields.args && message.fields.args[0] === "inserted") {
          // Replace by _id: a repeated insert must not list the room twice.
          const tempUserRooms = userRooms
            ?.filter((room) => room._id !== message.fields.args[1]._id)
            .concat(message.fields.args[1]);
          updateUserRooms(tempUserRooms ?? []);
        } else if (message.fields && message.fields.args && message.fields.args[0] === "removed") {
          updateUserRooms((userRooms ?? []).filter((room) => room._id !== message.fields.args[1]._id));
        }
      }
    },
    [userRooms, updateUserRooms],
  );

  // A room the user has just been added to (or created) arrives as a subscriptions-changed
  // "inserted"; the server sends no rooms-changed for it, so without a refetch the room only
  // appeared in the list after a reload. The list also needs the itinerary's channel list
  // from WordPress (it shows rooms present in both), which is fetched only at connect, so
  // refresh that too. Debounced: adding several users fires a burst.
  const refetchRoomsTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const refetchRoomsSoon = useCallback(() => {
    clearTimeout(refetchRoomsTimer.current);
    refetchRoomsTimer.current = setTimeout(() => {
      getRooms();
      bs().pullRocketChatChannels();
    }, 300);
  }, [getRooms]);
  useEffect(() => () => clearTimeout(refetchRoomsTimer.current), []);

  const updateSubscription = useCallback(
    (message: any) => {
      if (message.msg === "changed") {
        const [action, subscription] = message.fields?.args ?? [];
        if (!subscription) return;
        if (action === "updated" || action === "inserted") {
          // Replace by _id on both: the stream can repeat an insert (one per open subscription).
          const tempUserSubscriptions = (userSubscriptions ?? [])
            .filter((s) => s._id !== subscription._id)
            .concat(subscription);
          updateUserSubscriptions(tempUserSubscriptions);
          if (subscription.rid && !userRooms?.some((room) => room._id === subscription.rid)) {
            refetchRoomsSoon();
          }
        } else if (action === "removed") {
          updateUserSubscriptions((userSubscriptions ?? []).filter((s) => s._id !== subscription._id));
        }
      }
    },
    [userSubscriptions, updateUserSubscriptions, userRooms, refetchRoomsSoon],
  );

  const handleNotification = useCallback(
    (message: any) => {
      if (message.msg === "changed") {
        if (message.fields.args && message.fields.args[0] && message.fields.args[0].payload) {
          let tempUserRooms: IRoom[] | undefined = [];
          userRooms?.forEach((room) => {
            if (
              room._id &&
              message.fields.args[0].payload.rid &&
              room._id === message.fields.args[0].payload.rid &&
              room.lastMessage
            ) {
              const updatedRoom: IRoom = {
                ...room,
                lastMessage: {
                  ...room.lastMessage,
                  msg: message.fields.args[0].payload.message?.msg ?? "",
                },
                // The EJSON shape rooms-changed delivers and the room list sorts on
                // (`lm.$date`). A bare number fell back to the room's creation time, and
                // this notification can land after rooms-changed, overwriting a good `lm`.
                // Never move it backwards either.
                lm: { $date: Math.max(Date.now(), Number(room.lm?.$date) || 0) },
              };

              tempUserRooms = [...(tempUserRooms ?? []), updatedRoom];
            } else {
              tempUserRooms = tempUserRooms?.concat(room);
            }
          });
          updateUserRooms(tempUserRooms);
          if (chatNotificationPreference === null || chatNotificationPreference) {
            createRocketSnack(message.fields.args[0]);
          }
        }
      }
    },
    [userRooms, updateUserRooms, chatNotificationPreference],
  );

  const subscribeToNotifications = useCallback(() => {
    if (getLiveToken() && isOpen()) {
      subscribeToStream("stream-notify-user", "8129", [`${getLiveToken()?.userId}/message`]);
    }
    if (getLiveToken() && isOpen()) {
      subscribeToStream("stream-notify-user", "8130", [`${getLiveToken()?.userId}/webrtc`]);
    }
    if (getLiveToken() && isOpen()) {
      subscribeToStream("stream-notify-user", "8131", [`${getLiveToken()?.userId}/notification`]);
    }
    if (getLiveToken() && isOpen()) {
      subscribeToStream("stream-notify-user", "8132", [`${getLiveToken()?.userId}/rooms-changed`]);
    }
    if (getLiveToken() && isOpen()) {
      subscribeToStream("stream-notify-user", "8133", [`${getLiveToken()?.userId}/subscriptions-changed`]);
    }
    setLoadingChat(false);
  }, [isOpen, subscribeToStream]);

  const handleMessages = useCallback(
    (message: any) => {
      if (message.msg === "ping") {
        try {
          if (rocketWebsocket && isOpen()) {
            setLoadingChat(false);
            rocketWebsocket.send(
              JSON.stringify({
                msg: "pong",
              }),
            );
          } else {
            spWarnTags("Websocket not open, cannot send pong", ["rocketchat"]);
          }
        } catch {
          spWarnTags("Could not send pong, did the websocket close?", ["rocketchat"]);
        }
        return;
      } else if (message.msg === "connected") {
        spWarnTags("RCPROVIDER | Successful connection to Rocket Chat", ["rocketchat"]);
        useBoundStore.setState({ rocketSocketStatus: RocketSocketStatus.CONNECTED });
        try {
          if (rocketWebsocket && isOpen()) {
            spWarnTags("RCPROVIDER | Logging in to Rocket Chat", ["rocketchat"]);
            rocketWebsocket.send(
              constructMethod("login", [{ resume: getLiveToken()?.authToken }], RocketMessageId.login),
            );
          } else {
            spWarnTags("Websocket not open, cannot log in", ["rocketchat"]);
          }
        } catch (error) {
          spErrorTags("Error logging in to Rocket Chat", ["rocketchat"], error);
        }
        return;
      } else if (message.id && message.id === RocketMessageId.login && message.msg === "result" && message.result) {
        // Subscribing and refetching hang off the AUTHENTICATED status, not off this branch.
        spWarnTags("RCPROVIDER | Logged into Rocket Chat", ["rocketchat"]);
        resetReconnectBackoff();
        useBoundStore.setState({ rocketSocketStatus: RocketSocketStatus.AUTHENTICATED });
      } else if (message.id && message.id === RocketMessageId.login && message.msg === "result" && message.error) {
        // The server refused the credential (an expired or revoked session). Without this the
        // socket stayed connected but logged out, with no rooms and no retry. Dropping the
        // tokens hands over to the token retry loop, which fetches new ones and reconnects.
        spWarnTags("RCPROVIDER | chat login refused, fetching new chat credentials", ["rocketchat"], message.error);
        useRocketchatUiStore.getState().setLiveToken(undefined);
        useBoundStore.setState({ rcTokens: [] });
      } else if (message.id && message.id === RocketMessageId.getRooms && message.msg === "result") {
        if (message.result) {
          updateUserRooms(message.result?.update);
        } else {
          spErrorTags("Error fetching userRooms", ["rocketchat"]);
        }
      } else if (message.id && message.id === RocketMessageId.getSubscribedRooms && message.msg === "result") {
        if (message.result) {
          updateUserSubscriptions(message.result?.update);
        } else {
          spErrorTags("Error fetching userSubscriptions", ["rocketchat"]);
        }
      } else if (message.collection === "stream-notify-user") {
        if (message.fields.eventName === `${getLiveToken()?.userId}/message`) {
          // No handler wired up for this event yet.
        }
        if (message.fields.eventName === `${getLiveToken()?.userId}/webrtc`) {
          // No handler wired up for this event yet.
        }
        if (message.fields.eventName === `${getLiveToken()?.userId}/notification`) {
          try {
            handleNotification(message);
          } catch (e) {
            spErrorTags("Error in stream-notify-user/notification", ["rocketchat"], e);
          }
        }
        if (message.fields.eventName === `${getLiveToken()?.userId}/rooms-changed`) {
          try {
            updateRoom(message);
          } catch (e) {
            spErrorTags("Error in stream-notify-user/rooms-changed", ["rocketchat"], e);
          }
        }
        if (message.fields.eventName === `${getLiveToken()?.userId}/subscriptions-changed`) {
          try {
            updateSubscription(message);
          } catch (e) {
            spErrorTags("Error in stream-notify-user/subscriptions-changed", ["rocketchat"], e);
          }
        }
      }
    },
    [
      rocketWebsocket,
      updateUserRooms,
      updateUserSubscriptions,
      updateRoom,
      updateSubscription,
      handleNotification,
      isOpen,
    ],
  );

  // This useEffect will:
  // This handles all the messages we get from the web socket
  useEffect(() => {
    if (!isLeader) return;
    if (rocketWebsocket) {
      rocketWebsocket.onmessage = (e) => {
        const message = JSON.parse(e.data);
        handleMessages(message);
      };
    } else {
      spLogTags("No websocket, cannot handle message", ["rocketchat"]);
    }
  }, [isLeader, rocketWebsocket, handleMessages]);

  // Rooms, subscriptions and channels refresh once per authenticated socket, so a first
  // connect, a cold start and a reconnect after an outage all take the same path.
  const refreshedSocket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!isLeader) return;
    refreshedSocket.current = null;
  }, [isLeader, selectedOrgName, wpUserId]);

  useEffect(() => {
    if (!isLeader) return;
    if (rocketSocketStatus !== RocketSocketStatus.AUTHENTICATED || !rocketWebsocket) return;
    if (refreshedSocket.current === rocketWebsocket) return;
    refreshedSocket.current = rocketWebsocket;
    spWarnTags("RCPROVIDER | socket authenticated, refreshing rooms, subscriptions and channels", ["rocketchat"]);
    subscribeToNotifications();
    getRooms();
    getSubscribedRooms();
    bs().pullRocketChatChannels();
  }, [isLeader, rocketSocketStatus, rocketWebsocket, subscribeToNotifications, getRooms, getSubscribedRooms]);

  const setRooms = useCallback(
    (roomId: string) => {
      // Not 100% sure what this function is for, refactored to match newer functions, probably rename?
      const rooms = userRooms?.filter((room) => room._id !== roomId);
      updateUserRooms(rooms ?? []);
    },
    [userRooms, updateUserRooms],
  );

  const selectedItineraryUnreadCount = useMemo(() => {
    if (userSubscriptions && rocketChannels) {
      const thisItinRoomIds = rocketChannels.map((channel) => {
        return channel.channel_id;
      });
      const thisItineraryUnread = userSubscriptions.filter((sub) => {
        return sub.alert && thisItinRoomIds.includes(sub.rid);
      });
      return thisItineraryUnread.length;
    } else {
      return 0;
    }
  }, [userSubscriptions, rocketChannels]);

  useEffect(() => {
    if (!isLeader) return;
    bs().setChatUnreadCount(selectedItineraryUnreadCount);
  }, [isLeader, selectedItineraryUnreadCount]);

  return (
    <RocketChatContext.Provider
      value={{
        userRooms: userRooms ?? [],
        roomsFetched,
        userSubscriptions: userSubscriptions ?? [],
        rcUserList: rcUserList ?? [],
        rcUserInfo,
        createDirectChannel,
        openChat,
        loadingChat,
        setLoadingChat,
        isActivated,
        getRooms,
        setRooms,
        getSubscribedRooms,
        markAsRead,
        selectedItineraryUnreadCount,
      }}
    >
      {props.children}
    </RocketChatContext.Provider>
  );
};

export const useRocketChatContext = () => {
  return useContext(RocketChatContext);
};
export const getChatTypeFromSectionId = (section_id: number) => {
  const chatTypes = {
    1: "Group",
    2: "Flight",
    3: "Accommodation",
    4: "HireCar",
    5: "Transport",
    8: "Activity",
    9: "ParkingBooking",
    10: "VehicleAllocation",
  };

  return chatTypes[section_id as keyof typeof chatTypes] || "User";
};
export const getSectionIdFromChatType = (chatType: string) => {
  const chatTypeMap: { [key: string]: string } = {
    Group: "1",
    Flight: "2",
    Accommodation: "3",
    HireCar: "4",
    Transport: "5",
    Activity: "8",
    ParkingBooking: "9",
    VehicleAllocation: "10",
  };

  return chatTypeMap[chatType] || "0";
};
